import { useEffect, useState } from "react";
import { Icon } from "../../kit";
import { useCaps } from "../CapsProvider";
import { VariationCategory, useVariations } from "../sections/custom/variationShared";

/**
 * Customer-facing Variations grid (v32, iJewel `VariationConfiguratorGridUiPlugin`).
 * The buyer-side product-customization surface: category tabs across the top, a
 * grid of thumbnail/color swatches per category, click-to-apply. No authoring
 * controls — just the choices a shopper makes. Drives the same `applyVariation`
 * RPC as the editor control; the selected swatch is highlighted and shows a
 * spinner while the material/object loads.
 */
export function VariationsGridPanel() {
  const caps = useCaps();
  const hasV32 = caps.version >= 32;
  const { state, applying, apply } = useVariations();
  const [active, setActive] = useState(0);

  const categories = state?.categories ?? [];
  // Keep the active tab in range as categories load/clear.
  useEffect(() => {
    if (active >= categories.length) setActive(0);
  }, [categories.length, active]);

  if (!hasV32) {
    return (
      <div className="flex flex-col gap-3 p-3">
        <div className="flex items-center gap-2 rounded-lg bg-ed-row p-2.5">
          <Icon name="grid_view" size={18} className="text-ed-accent" />
          <span className="text-[12px] leading-snug text-ed-text-2">
            The variations configurator needs viewer contract v32 or newer.
          </span>
        </div>
      </div>
    );
  }

  if (categories.length === 0) {
    return (
      <div className="flex flex-col gap-3 p-3">
        <div className="flex items-center gap-2 rounded-lg bg-ed-row p-2.5">
          <Icon name="grid_view" size={18} className="text-ed-accent" />
          <span className="text-[12px] leading-snug text-ed-text-2">
            Load a variation config (Configurators → Variation Configurator) to let customers
            switch metals, gems, and parts here.
          </span>
        </div>
      </div>
    );
  }

  const cat = categories[Math.min(active, categories.length - 1)];

  return (
    <div className="flex min-h-0 flex-col">
      {/* Category tabs */}
      {categories.length > 1 ? (
        <div className="scroll-thin flex shrink-0 gap-1 overflow-x-auto border-b border-ed-border px-3 py-2">
          {categories.map((c, i) => (
            <button
              key={c.name}
              onClick={() => setActive(i)}
              className={`shrink-0 rounded-full border px-2.5 py-1 text-[12px] font-medium transition-colors ${
                i === active
                  ? "border-ed-accent bg-ed-active text-ed-text"
                  : "border-ed-border bg-white text-ed-text-2 hover:border-ed-accent"
              }`}
            >
              {c.title || c.name}
            </button>
          ))}
        </div>
      ) : null}

      <div className="scroll-thin flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto p-3">
        <div className="flex items-baseline justify-between">
          <span className="text-[13px] font-semibold text-ed-text">{cat.title || cat.name}</span>
          <span className="text-[12px] text-ed-text-3">{cat.type}</span>
        </div>
        <VariationCategory
          cat={cat}
          tileSize={64}
          showTitle={false}
          showLabels
          applyingItem={applying[cat.name] ?? null}
          onApply={(i) => apply(cat.name, i)}
        />
      </div>
    </div>
  );
}
